/**
 * ScreenGaze — on-screen gaze → content-region matching for ONLINE exams
 * (0704 §5.5 digital target). The screen analog of the physical homography→zone
 * path: instead of "which paper zone", we ask "which content region (title /
 * instruction / question / answer) is the gaze on, right now".
 *
 * Inputs (both real-time, NOT post-processing per CEO 2026-07-08):
 *   - gaze point in screen-normalized [0,1] coords  ← BlazeGaze / WebEyeTrack
 *   - content regions with roles                    ← authoring tool / exam DOM
 *
 * Delivers the two things that matter here:
 *   1. WHAT was looked at / NOT looked at, and in what ORDER (CEO's #1 ask for
 *      eye-tracking — more important than reaction time).
 *   2. A sustained-off-content-fixation signal — the phone-on-screen cheat cue
 *      (gaze fixed on a screen spot that holds no active content). Observation
 *      only; never a verdict.
 *
 * Pure logic, no DOM. The BlazeGaze capture + DOM region extraction are the
 * input plumbing that feed this.
 */

export type RegionRole = 'title' | 'instruction' | 'question' | 'answer' | 'image' | 'other';

export interface ScreenRegion {
  id: string;
  role: RegionRole;
  /** Rect in screen-normalized [0,1] coords. */
  rect: { x: number; y: number; w: number; h: number };
}

export interface RegionLikelihood {
  id: string;
  role: RegionRole | 'off';
  p: number;
}

export interface ScreenGazeMatch {
  /** Distribution over regions + trailing 'off' (gaze on no content). */
  distribution: RegionLikelihood[];
  top: RegionLikelihood;
  /** True if the gaze point is inside some region (with margin). */
  onContent: boolean;
  entropy: number;
}

function centerOf(r: ScreenRegion) {
  return { x: r.rect.x + r.rect.w / 2, y: r.rect.y + r.rect.h / 2 };
}
function insideRect(px: number, py: number, r: ScreenRegion, m: number) {
  return px >= r.rect.x - m && px <= r.rect.x + r.rect.w + m &&
         py >= r.rect.y - m && py <= r.rect.y + r.rect.h + m;
}
function clamp01(x: number) { return x < 0 ? 0 : x > 1 ? 1 : x; }

export interface MatchOpts {
  /** Softmax kernel width (screen-normalized distance). */
  tau?: number;
  /** Inclusion margin around a region rect. */
  margin?: number;
  /** Pseudo-distance for the 'off' bucket. */
  offDist?: number;
}

/** Per-frame: which content region is the gaze on (distribution + top + onContent). */
export function matchScreenGaze(
  gaze: { x: number; y: number }, regions: ScreenRegion[], opts: MatchOpts = {},
): ScreenGazeMatch {
  const tau = opts.tau ?? 0.12;
  const margin = opts.margin ?? 0.02;
  const offDist = opts.offDist ?? 0.22;

  const raw: Array<{ id: string; role: RegionRole | 'off'; score: number }> = [];
  let onContent = false;
  for (const r of regions) {
    const c = centerOf(r);
    const dx = gaze.x - c.x, dy = gaze.y - c.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    raw.push({ id: r.id, role: r.role, score: Math.exp(-dist / tau) });
    if (insideRect(gaze.x, gaze.y, r, margin)) onContent = true;
  }
  raw.push({ id: 'off', role: 'off', score: Math.exp(-offDist / tau) });

  const total = raw.reduce((s, r) => s + r.score, 0) || 1;
  const distribution = raw.map((r) => ({ id: r.id, role: r.role, p: r.score / total }));
  let top = distribution[0];
  for (const d of distribution) if (d.p > top.p) top = d;
  let entropy = 0;
  for (const d of distribution) if (d.p > 0) entropy -= d.p * Math.log2(d.p);
  return { distribution, top, onContent, entropy };
}

export interface RegionDwell {
  id: string;
  role: RegionRole;
  dwellMs: number;
  firstLookMs: number | null;
  looks: number; // number of separate entries
}

export interface ScreenGazeReport {
  perRegion: RegionDwell[];
  /** Region ids in first-look order. */
  order: string[];
  /** Regions never looked at (dwell 0) — CEO's "what was NOT looked at". */
  notLookedAt: Array<{ id: string; role: RegionRole }>;
  /** ms with gaze off all content. */
  offContentMs: number;
  offContentRatio: number;
  /** Sustained off-content fixation (phone-on-screen cue). Observation only. */
  sustainedOffContentFixation: { active: boolean; ms: number; point: { x: number; y: number } | null };
  /**
   * Repeated glances to a consistent off-content location — "keeps looking at
   * a spot there's no content reason to look at" (e.g. a phone below the
   * monitor, glanced at periodically). count = glances landing in the dominant
   * off-content cluster. Observation only, never a verdict.
   */
  repeatedOffContentGlances: { count: number; totalMs: number; point: { x: number; y: number } | null };
}

export interface SessionOpts extends MatchOpts {
  /** Off-content fixation must persist ≥ this to flag (ms). */
  offContentMinMs?: number;
  /** Max gaze spread (screen-norm) during the off-content run to count as a fixation. */
  fixationRadius?: number;
  /** A single off-content run must last ≥ this to count as a "glance". */
  minGlanceMs?: number;
  /** Glances within this distance (screen-norm) are the same off-content location. */
  glanceClusterRadius?: number;
}

/**
 * Accumulates per-region dwell, first-look order, not-looked-at set, and the
 * sustained-off-content-fixation cue across a session. Feed each gaze sample.
 */
export class ScreenGazeSession {
  private dwell = new Map<string, RegionDwell>();
  private orderList: string[] = [];
  private lastT: number | null = null;
  private offMs = 0;
  private totalMs = 0;
  // off-content fixation run
  private offRun = { ms: 0, sx: 0, sy: 0, n: 0, maxDev: 0 };
  private lastInsideIds = new Set<string>();
  /** Completed off-content glances (mean point + duration). */
  private glances: Array<{ x: number; y: number; ms: number }> = [];

  constructor(private opts: SessionOpts = {}) {}

  /** Push one gaze sample. gaze=null means gaze/ face lost this frame. */
  update(t: number, gaze: { x: number; y: number } | null, regions: ScreenRegion[]): void {
    // ensure dwell entries exist for all regions seen
    for (const r of regions) {
      if (!this.dwell.has(r.id)) this.dwell.set(r.id, { id: r.id, role: r.role, dwellMs: 0, firstLookMs: null, looks: 0 });
    }
    const dt = this.lastT == null ? 0 : Math.max(0, t - this.lastT);
    this.lastT = t;
    this.totalMs += dt;
    if (!gaze) { this.closeGlance(); this.lastInsideIds = new Set(); return; }

    const m = this.opts.margin ?? 0.02;
    const insideNow = new Set<string>();
    for (const r of regions) if (insideRect(gaze.x, gaze.y, r, m)) insideNow.add(r.id);

    if (insideNow.size > 0) {
      // on content — an off-content run (if any) just ended → record the glance
      this.closeGlance();
      for (const id of Array.from(insideNow)) {
        const d = this.dwell.get(id)!;
        d.dwellMs += dt;
        if (d.firstLookMs == null) { d.firstLookMs = t; this.orderList.push(id); }
        if (!this.lastInsideIds.has(id)) d.looks += 1; // new entry
      }
    } else {
      // off content
      this.offMs += dt;
      this.offRun.ms += dt;
      this.offRun.n += 1;
      this.offRun.sx += gaze.x; this.offRun.sy += gaze.y;
      const mx = this.offRun.sx / this.offRun.n, my = this.offRun.sy / this.offRun.n;
      const dev = Math.hypot(gaze.x - mx, gaze.y - my);
      if (dev > this.offRun.maxDev) this.offRun.maxDev = dev;
    }
    this.lastInsideIds = insideNow;
  }

  private resetOffRun() { this.offRun = { ms: 0, sx: 0, sy: 0, n: 0, maxDev: 0 }; }

  /** Close the current off-content run into a glance (if long enough), then reset. */
  private closeGlance() {
    const minGlance = this.opts.minGlanceMs ?? 150;
    if (this.offRun.n > 0 && this.offRun.ms >= minGlance) {
      this.glances.push({ x: this.offRun.sx / this.offRun.n, y: this.offRun.sy / this.offRun.n, ms: this.offRun.ms });
    }
    this.resetOffRun();
  }

  /** Largest cluster of off-content glances (repeated looking at one spot). */
  private repeatedGlances() {
    const radius = this.opts.glanceClusterRadius ?? 0.08;
    const minGlance = this.opts.minGlanceMs ?? 150;
    // include the in-progress run as a provisional glance
    const all = [...this.glances];
    if (this.offRun.n > 0 && this.offRun.ms >= minGlance) {
      all.push({ x: this.offRun.sx / this.offRun.n, y: this.offRun.sy / this.offRun.n, ms: this.offRun.ms });
    }
    let best = { count: 0, totalMs: 0, point: null as { x: number; y: number } | null };
    for (const seed of all) {
      const near = all.filter((g) => Math.hypot(g.x - seed.x, g.y - seed.y) <= radius);
      if (near.length > best.count) {
        const n = near.length;
        best = {
          count: n,
          totalMs: Math.round(near.reduce((s, g) => s + g.ms, 0)),
          point: { x: +(near.reduce((s, g) => s + g.x, 0) / n).toFixed(3), y: +(near.reduce((s, g) => s + g.y, 0) / n).toFixed(3) },
        };
      }
    }
    return best;
  }

  report(): ScreenGazeReport {
    const minMs = this.opts.offContentMinMs ?? 4000;
    const fixR = this.opts.fixationRadius ?? 0.06;
    const perRegion = Array.from(this.dwell.values());
    const notLookedAt = perRegion.filter((d) => d.dwellMs === 0).map((d) => ({ id: d.id, role: d.role }));
    const fixActive = this.offRun.ms >= minMs && this.offRun.n > 0 && this.offRun.maxDev <= fixR;
    const point = this.offRun.n > 0
      ? { x: clamp01(this.offRun.sx / this.offRun.n), y: clamp01(this.offRun.sy / this.offRun.n) }
      : null;
    return {
      perRegion,
      order: [...this.orderList],
      notLookedAt,
      offContentMs: Math.round(this.offMs),
      offContentRatio: this.totalMs > 0 ? +(this.offMs / this.totalMs).toFixed(3) : 0,
      sustainedOffContentFixation: { active: fixActive, ms: Math.round(this.offRun.ms), point },
      repeatedOffContentGlances: this.repeatedGlances(),
    };
  }

  reset() {
    this.dwell.clear(); this.orderList = []; this.lastT = null;
    this.offMs = 0; this.totalMs = 0; this.resetOffRun(); this.lastInsideIds = new Set();
    this.glances = [];
  }
}
